/** @jsxImportSource @emotion/react */
import React from "react";
import {
    HistoryContainer,
    HistoryCard,
    HistoryCardHeader,
    HistoryCardDate,
    HistoryCardMeta,
    HistoryCardBody,
    HistoryBadgeRow,
    HistoryBadge,
    HistoryDetailRow,
    HistoryDetailItem,
    HistorySkeletonCard,
    HistorySkeletonLine,
    EmptyState,
} from "./EMTCTStyles";

/* ─── Helpers ─── */
const formatDate = function(dateStr) {
    if (!dateStr) return "—";
    try {
        return new Date(dateStr).toLocaleDateString("en-GB", {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch (e) { return dateStr; }
};

const hivBadge = function(result) {
    const val = (result || "").toLowerCase();
    if (val === "reactive" || val === "positive") {
        return { label: "HIV ⊕ Reactive", bg: "#ffebee", color: "#c62828", border: "#ef9a9a" };
    }
    if (val === "non_reactive" || val === "negative") {
        return { label: "HIV ⊖ Non-Reactive", bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" };
    }
    return { label: "HIV — Not recorded", bg: "#f5f5f5", color: "#90a4ae", border: "#e0e0e0" };
};

const syphilisBadge = function(result) {
    const val = (result || "").toLowerCase();
    if (val === "reactive") {
        return { label: "Syphilis Reactive", bg: "#fff3e0", color: "#e65100", border: "#ffcc80" };
    }
    if (val === "non_reactive") {
        return { label: "Syphilis Non-Reactive", bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" };
    }
    return null;
};

const hepBBadge = function(result) {
    const val = (result || "").toLowerCase();
    if (val === "positive" || val === "reactive") {
        return { label: "Hep B Positive", bg: "#fff3e0", color: "#e65100", border: "#ffcc80" };
    }
    if (val === "negative") {
        return { label: "Hep B Negative", bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" };
    }
    return null;
};

const counsellingTally = function(record) {
    const fields = [
        "pretest_counselling_done",
        "posttest_counselling_done",
        "adherence_counselling_done",
        "iycf_counselling_done",
        "referred_psychosocial",
    ];
    const done = fields.filter(function(f) { return !!record[f]; }).length;
    return done + " / " + fields.length + " done";
};

/* ─── Skeleton ─── */
const HistorySkeleton = function() {
    return (
        <HistoryContainer>
            {[1, 2, 3].map(function(i) {
                return (
                    <HistorySkeletonCard key={i}>
                        <HistorySkeletonLine w="140px" h="16px" />
                        <HistorySkeletonLine w="220px" />
                        <HistorySkeletonLine w="80%" />
                        <HistorySkeletonLine w="60%" />
                    </HistorySkeletonCard>
                );
            })}
        </HistoryContainer>
    );
};

/* ─── Main Component ─── */
export const EMTCTHistoryTimeline = function(props) {
    const records = props.records || [];
    const loading = props.loading;

    if (loading) {
        return <HistorySkeleton />;
    }

    if (records.length === 0) {
        return (
            <EmptyState>
                <div className="empty-icon">🧬</div>
                <div className="empty-title">No eMTCT Records Yet</div>
                <div className="empty-desc">
                    Switch to the Record tab to document the first eMTCT assessment for this patient.
                </div>
            </EmptyState>
        );
    }

    const sorted = records.slice().sort(function(a, b) {
        return new Date(b.updated_at) - new Date(a.updated_at);
    });

    return (
        <HistoryContainer>
            {sorted.map(function(record) {
                const hiv = hivBadge(record.hiv_test_result);
                const syph = syphilisBadge(record.syphilis_result);
                const hep = hepBBadge(record.hepatitis_b_result);
                const isPos = (record.hiv_test_result || "").toLowerCase() === "reactive" ||
                              (record.hiv_test_result || "").toLowerCase() === "positive";

                return (
                    <HistoryCard key={record.id}>
                        <HistoryCardHeader>
                            <HistoryCardDate>
                                {formatDate(record.updated_at)}
                                {record.anc_number && (
                                    <span style={{ fontWeight: 400, marginLeft: 10, color: "#5a7fa0" }}>
                                        ANC# {record.anc_number}
                                    </span>
                                )}
                            </HistoryCardDate>
                            <HistoryCardMeta>
                                {record.booking_date && (
                                    <span>Booking: {formatDate(record.booking_date)}</span>
                                )}
                                {record.saved_by && (
                                    <span style={{ marginLeft: 10 }}>By: {record.saved_by}</span>
                                )}
                            </HistoryCardMeta>
                        </HistoryCardHeader>

                        <HistoryCardBody>
                            {/* ─── Result badges ─── */}
                            <HistoryBadgeRow>
                                <HistoryBadge bg={hiv.bg} color={hiv.color} border={hiv.border}>
                                    {hiv.label}
                                </HistoryBadge>
                                {syph && (
                                    <HistoryBadge bg={syph.bg} color={syph.color} border={syph.border}>
                                        {syph.label}
                                    </HistoryBadge>
                                )}
                                {hep && (
                                    <HistoryBadge bg={hep.bg} color={hep.color} border={hep.border}>
                                        {hep.label}
                                    </HistoryBadge>
                                )}
                            </HistoryBadgeRow>

                            {/* ─── HIV+ clinical details ─── */}
                            {isPos && (
                                <HistoryDetailRow>
                                    {record.woman_art_regimen && (
                                        <HistoryDetailItem>
                                            <strong>ART:</strong> {record.woman_art_regimen}
                                        </HistoryDetailItem>
                                    )}
                                    {record.woman_who_stage && (
                                        <HistoryDetailItem>
                                            <strong>WHO Stage:</strong> {record.woman_who_stage}
                                        </HistoryDetailItem>
                                    )}
                                    {record.cd4_count != null && (
                                        <HistoryDetailItem>
                                            <strong>CD4:</strong> {record.cd4_count} cells/µL
                                        </HistoryDetailItem>
                                    )}
                                    {record.viral_load_count != null && (
                                        <HistoryDetailItem>
                                            <strong>VL:</strong> {record.viral_load_count} copies/mL
                                        </HistoryDetailItem>
                                    )}
                                    {record.infant_feeding_option && (
                                        <HistoryDetailItem>
                                            <strong>Infant feeding:</strong>{" "}
                                            {record.infant_feeding_option.replace(/_/g, " ")}
                                        </HistoryDetailItem>
                                    )}
                                </HistoryDetailRow>
                            )}

                            {/* ─── Partner + counselling summary ─── */}
                            <HistoryDetailRow>
                                <HistoryDetailItem>
                                    <strong>Partner tested:</strong> {record.partner_tested ? "Yes" : "No"}
                                </HistoryDetailItem>
                                <HistoryDetailItem>
                                    <strong>Counselling:</strong> {counsellingTally(record)}
                                </HistoryDetailItem>
                            </HistoryDetailRow>
                        </HistoryCardBody>
                    </HistoryCard>
                );
            })}
        </HistoryContainer>
    );
};
